<template>
  <div
      :class="['layer-row-block', { 'layer-row-block--child': isChild, 'layer-row-block--dragging': isDraggingWithSelection }]"
  >
    <div
        :id="layer.Id + '#layerId'"
        :class="rowClasses"
        :style="rowStyle"
        class="layer-wrapper"
        @click.stop="$emit('select', layer, parentGroup, $event)"
        @contextmenu.prevent="onChildContextMenu($event)"
        @dragenter="onGroupDragEnter"
        @dragleave="onGroupDragLeave"
        @dragend="clearDropTarget"
        @drop="clearDropTarget"
    >
      <button
          v-if="isGroup"
          type="button"
          class="layer-chevron-btn"
          :aria-label="isGroupExpanded ? `Collapse ${layer.Name}` : `Expand ${layer.Name}`"
          :aria-expanded="isGroupExpanded ? 'true' : 'false'"
          @click.stop="$emit('toggle-group', layer)"
      >
        <layer-icon
            :name="isGroupExpanded ? 'chevron-down' : 'chevron-right'"
            :size="13"
        />
      </button>
      <span
          v-else
          class="layer-chevron-slot"
      />

      <span class="layer-type-icon">
        <layer-icon
            :name="layerIcon"
            :size="15"
        />
      </span>

      <input
          ref="nameInput"
          v-model.trim="layer.Name"
          :readonly="!layer.isEditableForRightPanel"
          :class="{ 'layer-input--editing': layer.isEditableForRightPanel }"
          class="layer-input"
          type="text"
          aria-label="Layer name"
          @dblclick.stop="enableRename(layer, $event)"
          @blur.stop="onRenameBlur"
          @keyup.enter="onRenameEnter"
          @keydown="$emit('validate-input', $event)"
      >

      <div class="layer-actions">
        <button
            type="button"
            class="layer-action"
            :class="{ 'layer-action--persist': layer.IsVisible === false }"
            :aria-label="layer.IsVisible === false ? 'Show layer' : 'Hide layer'"
            :title="layer.IsVisible === false ? 'Show layer' : 'Hide layer'"
            @click.stop="$emit('toggle-visibility', layer, parentGroup)"
        >
          <layer-icon
              :name="layer.IsVisible === false ? 'eye-off' : 'eye'"
              :size="14"
          />
        </button>
        <button
            type="button"
            class="layer-action"
            aria-label="Duplicate layer"
            title="Duplicate layer"
            @click.stop="$emit('duplicate', layer, parentGroup)"
        >
          <layer-icon
              name="duplicate"
              :size="14"
          />
        </button>
        <button
            type="button"
            class="layer-action"
            :class="{ 'layer-action--persist': layer.locked }"
            :aria-label="layer.locked ? 'Unlock layer' : 'Lock layer'"
            :title="layer.locked ? 'Unlock layer' : 'Lock layer'"
            @click.stop="$emit('lock', layer, parentGroup)"
        >
          <layer-icon
              :name="layer.locked ? 'lock' : 'lock-open'"
              :size="14"
          />
        </button>
        <button
            type="button"
            class="layer-action"
            aria-label="Delete layer"
            title="Delete layer"
            @click.stop="$emit('delete', layer, parentGroup, isChild)"
        >
          <layer-icon
              name="trash"
              :size="14"
          />
        </button>
      </div>
    </div>
    <draggable
        v-if="isGroup"
        v-model="layer.children"
        :group="dragGroupOptions"
        :animation="150"
        :class="['layer-group-children', { 'layer-group-children--collapsed': !isGroupExpanded }]"
        @start="onChildDragStart"
        @end="onChildDragEnd"
    >
      <layer-row
          v-for="child in layer.children"
          v-show="isGroupExpanded"
          :key="child.Id"
          :layer="child"
          :parent-group="layer"
          :depth="depth + 1"
          :is-child="true"
          :drag-group-options="dragGroupOptions"
          :dragging-layer-ids="draggingLayerIds"

          @select="(c, pg, ev) => $emit('select', c, pg || layer, ev)"
          @contextmenu="(e, c, pg, ic) => $emit('contextmenu', e, c, pg || layer, ic)"
          @toggle-visibility="(c, pg) => $emit('toggle-visibility', c, pg || layer)"
          @lock="(c, pg) => $emit('lock', c, pg || layer)"
          @delete="(c, pg, ic) => $emit('delete', c, pg || layer, ic)"
          @duplicate="(c, pg) => $emit('duplicate', c, pg || layer)"
          @toggle-group="(c) => $emit('toggle-group', c)"
          @validate-input="(e) => $emit('validate-input', e)"
          @drag-start="(payload) => $emit('drag-start', payload)"
          @drag-end="(payload) => $emit('drag-end', payload)"
      />
    </draggable>

  </div>
</template>

<script>
import draggable from 'vuedraggable';
import LayerIcon from '@/components/Layer/LayerIcon.vue';
import { getLayerIconName } from '@/components/Layer/layerIcons';

export default {
  name:       'LayerRow',
  components: { draggable, LayerIcon },
  props:      {
    layer: {
      type:     Object,
      required: true,
    },
    parentGroup: {
      type:    Object,
      default: null,
    },
    depth: {
      type:    Number,
      default: 0,
    },
    isChild: {
      type:    Boolean,
      default: false,
    },
    dragGroupOptions: {
      type:    Object,
      default: () => ({ name: 'layers', pull: true, put: true }),
    },
    draggingLayerIds: {
      type:    Array,
      default: () => [],
    },
  },
  data() {
    return {
      isDropTarget: false,
    };
  },
  mounted() {
    document.addEventListener('dragend', this.clearDropTarget);
  },
  beforeDestroy() {
    document.removeEventListener('dragend', this.clearDropTarget);
    document.removeEventListener('pointerdown', this.onRenameOutside, true);
  },
  computed: {
    isDraggingWithSelection() {
      return this.draggingLayerIds.includes(this.layer.Id);
    },
    isGroup() {
      return this.layer.type === 'group';
    },
    isGroupExpanded() {
      return this.layer.isGroupExpanded !== false;
    },
    isChildOfSelectedGroup() {
      return this.isChild && this.parentGroup && this.parentGroup.isSelected && !this.layer.isSelected;
    },
    rowClasses() {
      return {
        'active-layer':                      !!this.layer.isSelected,
        'layer-wrapper--in-group-selection': this.isChildOfSelectedGroup,
        'layer-wrapper--group':              this.isGroup,
        'layer-wrapper--child':              this.isChild,
        'layer-wrapper--hidden':             this.layer.IsVisible === false,
        'layer-wrapper--locked':             !!this.layer.locked,
        'layer-wrapper--renaming':           !!this.layer.isEditableForRightPanel,
        'layer-wrapper--drop-target':        this.isDropTarget,
      };
    },
    rowStyle() {
      if (this.depth <= 0) {
        return {};
      }
      return {
        paddingLeft: `${8 + (this.depth * 16)}px`,
      };
    },
    layerIcon() {
      return getLayerIconName(this.layer);
    },
  },
  methods: {
    onGroupDragEnter() {
      if (!this.isGroup || this.$el.classList.contains('sortable-chosen')) { return; }
      this.isDropTarget = true;
    },
    onGroupDragLeave(event) {
      if (!this.isGroup) { return; }
      const next = event.relatedTarget;
      if (next && event.currentTarget.contains(next)) { return; }
      this.isDropTarget = false;
    },
    clearDropTarget() {
      this.isDropTarget = false;
    },
    enableRename(layer, event) {
      if (layer.Name === 'z-border') { return; }
      layer.isEditableForRightPanel = true;
      const input = event.currentTarget;
      this.$nextTick(() => {
        if (!input) { return; }
        input.focus();
        const placeCaret = () => {
          const end = input.value.length;
          input.setSelectionRange(end, end);
        };
        placeCaret();
        requestAnimationFrame(placeCaret);
      });
      document.removeEventListener('pointerdown', this.onRenameOutside, true);
      document.addEventListener('pointerdown', this.onRenameOutside, true);
    },
    onRenameBlur(event) {
      this.finishRename(event.target, false);
    },
    onRenameEnter(event) {
      this.finishRename(event.target, true);
    },
    onRenameOutside(event) {
      const input = this.$refs.nameInput;
      if (input && event.target === input) { return; }
      this.finishRename(input, true);
    },
    finishRename(input, shouldBlur) {
      const field = input || this.$refs.nameInput;
      this.clearNameSelection(field);
      this.layer.isEditableForRightPanel = false;
      document.removeEventListener('pointerdown', this.onRenameOutside, true);
      if (shouldBlur && field && document.activeElement === field) {
        field.blur();
      }
    },
    clearNameSelection(input) {
      if (input && !input.readOnly && typeof input.setSelectionRange === 'function') {
        const end = (input.value || '').length;
        input.setSelectionRange(end, end);
      }
      const selection = window.getSelection && window.getSelection();
      if (selection && selection.removeAllRanges) {
        selection.removeAllRanges();
      }
    },
    onChildContextMenu(e) {
      this.$emit('contextmenu', e, this.layer, this.parentGroup, this.isChild);
    },
    onChildDragStart(evt) {
      this.$emit('drag-start', { group: this.layer, event: evt });
    },
    onChildDragEnd(evt) {
      this.$emit('drag-end', { group: this.layer, event: evt });
    },
  },
};
</script>

<style lang="scss" scoped>
.layer-row-block--dragging {
  opacity: 0;
}
</style>
